/**
 * TutorialModal Component
 *
 * Displays a welcome tutorial with basic navigation and node creation instructions
 * Includes a "Don't show again" checkbox to allow users to dismiss permanently
 */

import { CSSProperties, useEffect, useState } from 'react';

interface TutorialModalProps {
  isVisible: boolean;
  onClose: () => void;
  onDismissPermanently: () => void;
}

function TutorialModal({ isVisible, onClose, onDismissPermanently }: TutorialModalProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Close on Escape key
  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  // Styles
  const overlayStyles: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(4px)',
  };

  const modalStyles: CSSProperties = {
    backgroundColor: 'var(--nb-bg-primary)',
    borderRadius: 'var(--nb-radius-lg)',
    padding: '0',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    border: '1px solid var(--nb-border-primary)',
  };

  const headerStyles: CSSProperties = {
    padding: '24px 32px',
    borderBottom: '1px solid var(--nb-border-primary)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-xl)',
    fontWeight: 700,
    margin: 0,
    color: 'var(--nb-text-primary)',
  };

  const contentStyles: CSSProperties = {
    padding: '24px 32px',
    overflowY: 'auto',
    flex: 1,
    color: 'var(--nb-text-primary)',
    lineHeight: '1.7',
  };

  const sectionStyles: CSSProperties = {
    marginBottom: '24px',
  };

  const sectionTitleStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-md)',
    fontWeight: 700,
    marginBottom: '12px',
    color: 'var(--nb-accent-primary)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const listStyles: CSSProperties = {
    listStyle: 'none',
    padding: '0',
    margin: '0',
  };

  const listItemStyles: CSSProperties = {
    marginBottom: '12px',
    paddingLeft: '24px',
    position: 'relative',
  };

  const bulletStyles: CSSProperties = {
    position: 'absolute',
    left: '0',
    top: '0',
    color: 'var(--nb-accent-primary)',
    fontWeight: 700,
  };

  const closeButtonStyles: CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: 'var(--nb-text-secondary)',
    padding: '4px 8px',
    lineHeight: 1,
    transition: 'color 0.2s',
  };

  const footerStyles: CSSProperties = {
    padding: '20px 32px',
    borderTop: '1px solid var(--nb-border-primary)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const checkboxContainerStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const checkboxStyles: CSSProperties = {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  };

  const checkboxLabelStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-secondary)',
    cursor: 'pointer',
  };

  const buttonStyles: CSSProperties = {
    backgroundColor: 'var(--nb-accent-primary)',
    color: 'var(--nb-text-on-accent)',
    border: 'none',
    borderRadius: 'var(--nb-radius-md)',
    padding: '10px 24px',
    fontSize: 'var(--nb-font-size-md)',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  };

  const shortcutKeyStyles: CSSProperties = {
    backgroundColor: 'var(--nb-bg-secondary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    padding: '2px 6px',
    fontSize: 'var(--nb-font-size-xs)',
    fontFamily: 'monospace',
    marginLeft: '8px',
  };

  return (
    <div style={overlayStyles} onClick={onClose}>
      <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyles}>
          <h2 style={titleStyles}>Welcome to EmberBoard!</h2>
          <button
            style={closeButtonStyles}
            onClick={onClose}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--nb-text-primary)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--nb-text-secondary)')}
            title="Close (Esc)"
          >
            ×
          </button>
        </div>

        <div style={contentStyles}>
          <p style={{ marginBottom: '24px', fontSize: 'var(--nb-font-size-md)' }}>
            Here's a quick guide to get you started with creating storyboards:
          </p>

          {/* Navigation Section */}
          <div style={sectionStyles}>
            <div style={sectionTitleStyles}>
              <span>🎯</span>
              <span>Navigation</span>
            </div>
            <ul style={listStyles}>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Pan the canvas by dragging with your mouse</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Zoom in/out using your mouse wheel or trackpad</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Press <span style={shortcutKeyStyles}>F</span> to frame/center the current beat</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Use the timeline at the bottom to navigate your sequence</div>
              </li>
            </ul>
          </div>

          {/* Node Creation Section */}
          <div style={sectionStyles}>
            <div style={sectionTitleStyles}>
              <span>✨</span>
              <span>Creating Beats</span>
            </div>
            <ul style={listStyles}>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Double-click anywhere to create a new beat</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Paste images from the clipboard to create a node</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Press <span style={shortcutKeyStyles}>B</span> to rapidly create a beat after the current one</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Drag and drop images onto the canvas to create beats automatically</div>
              </li>
            </ul>
          </div>

          {/* Editing Section */}
          <div style={sectionStyles}>
            <div style={sectionTitleStyles}>
              <span>✏️</span>
              <span>Editing</span>
            </div>
            <ul style={listStyles}>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Click a beat to edit its properties in the right panel</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Add images, script text, and camera overlays</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Drag beats to rearrange them in the timeline</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Use the Beat Shelf to store unused beats</div>
              </li>
            </ul>
          </div>

          {/* Tips Section */}
          <div style={sectionStyles}>
            <div style={sectionTitleStyles}>
              <span>💡</span>
              <span>Pro Tips</span>
            </div>
            <ul style={listStyles}>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Use <span style={shortcutKeyStyles}>J/K/L</span> keys for timeline navigation</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Hold <span style={shortcutKeyStyles}>Alt</span> + drag to duplicate beats</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Use the Reference Library for mood boards and inspiration</div>
              </li>
              <li style={listItemStyles}>
                <span style={bulletStyles}>•</span>
                <div>Export your storyboard as PDF, Fountain script, or EDL</div>
              </li>
            </ul>
          </div>
        </div>

        <div style={footerStyles}>
          <div style={checkboxContainerStyles}>
            <input
              type="checkbox"
              id="dont-show-again"
              style={checkboxStyles}
              checked={dontShowAgain}
              onChange={(e) => {
                setDontShowAgain(e.target.checked);
                // Only call onDismissPermanently when checking the box
                // Don't close the modal automatically
                if (e.target.checked) {
                  onDismissPermanently();
                }
              }}
            />
            <label htmlFor="dont-show-again" style={checkboxLabelStyles}>
              Don't show this again
            </label>
          </div>
          <button
            style={buttonStyles}
            onClick={onClose}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--nb-accent-secondary)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--nb-accent-primary)')}
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}

export default TutorialModal;
